export type Point = { x: number; y: number };
export type ViewTransform = { zoom: number; panX: number; panY: number };
export type FrameTransform = { cx: number; cy: number; rotation: number };

export function screenToDocument(point: Point, view: ViewTransform): Point {
  return { x: (point.x - view.panX) / view.zoom, y: (point.y - view.panY) / view.zoom };
}

export function documentToScreen(point: Point, view: ViewTransform): Point {
  return { x: point.x * view.zoom + view.panX, y: point.y * view.zoom + view.panY };
}

export function documentToLocal(point: Point, frame: FrameTransform): Point {
  const radians = (frame.rotation * Math.PI) / 180;
  const dx = point.x - frame.cx;
  const dy = point.y - frame.cy;
  return {
    x: dx * Math.cos(radians) + dy * Math.sin(radians),
    y: -dx * Math.sin(radians) + dy * Math.cos(radians),
  };
}

export function localToDocument(point: Point, frame: FrameTransform): Point {
  const radians = (frame.rotation * Math.PI) / 180;
  return {
    x: frame.cx + point.x * Math.cos(radians) - point.y * Math.sin(radians),
    y: frame.cy + point.x * Math.sin(radians) + point.y * Math.cos(radians),
  };
}

export function screenToLocal(point: Point, view: ViewTransform, frame: FrameTransform): Point {
  return documentToLocal(screenToDocument(point, view), frame);
}

export function localToScreen(point: Point, view: ViewTransform, frame: FrameTransform): Point {
  return documentToScreen(localToDocument(point, frame), view);
}

export function fitCanvasToViewport(
  viewportWidth: number,
  viewportHeight: number,
  canvasWidth: number,
  canvasHeight: number,
  padding = 96,
): ViewTransform {
  const zoom = Math.min(
    (viewportWidth - padding * 2) / canvasWidth,
    (viewportHeight - padding * 2) / canvasHeight,
    8,
  );
  const safeZoom = Math.max(0.01, zoom);
  return {
    zoom: safeZoom,
    panX: (viewportWidth - canvasWidth * safeZoom) / 2,
    panY: (viewportHeight - canvasHeight * safeZoom) / 2,
  };
}
